// Shared logic for AppointmentRequest reads — used by every UI surface that
// has to answer "is there a pending X request?" or "what state is the
// appointment's confirmation in?".
//
// Designed to be extended for new action types (reschedule/cancel/etc.) by
// reusing the generic `findPendingRequest` / `hasPendingRequest` helpers and
// adding an action-specific state machine alongside `getConfirmationState`.

import moment from 'moment';
import { APPOINTMENT_STATUS } from 'utils/constants';

// Backend request states that mean "no longer in flight". Anything outside
// this set (canonical 'pending' plus any future intermediate state the
// backend introduces) is treated as still active.
const TERMINAL_REQUEST_STATES = new Set(['completed']);

export const isRequestPending = (req): boolean =>
  !TERMINAL_REQUEST_STATES.has(req.state);

/**
 * Most recent in-flight request of the given action type, if any.
 * `appt.requests` is stored chronologically (oldest → newest), so we scan
 * from the end to return the latest entry without sorting.
 */
export const findPendingRequest = (
  appt,
  actionType,
) => {
  for (let i = appt.requests.length - 1; i >= 0; i -= 1) {
    const r = appt.requests[i];
    if (r.actionType === actionType && isRequestPending(r)) return r;
  }
  return undefined;
};

export const hasPendingRequest = (
  appt,
  actionType,
): boolean => findPendingRequest(appt, actionType) !== undefined;

// ─── Confirmation state machine ─────────────────────────────────────
// Drives the ConfirmationBadge + the Confirm button across the portal.
//
//   not-applicable  → confirmation is moot (cancelled or unscheduled)
//   confirmed       → appt.confirmed === true (office has confirmed)
//   pending-request → patient submitted a confirm request, office reviewing
//   pending         → nothing happened yet — Confirm button should be offered
export type ConfirmationState =
  | 'not-applicable'
  | 'confirmed'
  | 'pending-request'
  | 'pending';

// Statuses where the appointment isn't a confirmable slot — cancelled is
// closed, unscheduled has no time to confirm against. Both surface the same
// neutral "not applicable" state so every UI surface treats them identically.
const NON_CONFIRMABLE_STATUSES = new Set<string>([
  APPOINTMENT_STATUS.CANCELLED,
  APPOINTMENT_STATUS.UNSCHEDULED,
]);

/**
 * True once the appointment's slot has fully elapsed (start + duration < now)
 * or the backend has marked it completed. Prevents action UIs from offering
 * confirmation/reschedule on visits that are already in the past.
 */
export const isAppointmentPast = (appt): boolean => {
  if (appt.status === APPOINTMENT_STATUS.COMPLETED) return true;
  if (!appt.appointmentAt) return false;
  const duration = Number(appt.durationMinutes ?? 0);
  const start = moment.parseZone(appt.appointmentAt);

  if (!start.isValid()) return false;

  const end = start.clone().add(duration, 'minutes');
  return end.valueOf() < Date.now();
};

export const getConfirmationState = (appt): ConfirmationState => {
  if (NON_CONFIRMABLE_STATUSES.has(appt.status)) return 'not-applicable';
  // Past appointments: keep showing "Confirmed" if it was confirmed before
  // it happened, but skip the pending/request states — confirmation after
  // the fact is moot, so render the neutral em-dash instead.
  if (appt.confirmed) return 'confirmed';
  if (isAppointmentPast(appt)) return 'not-applicable';
  if (hasPendingRequest(appt, 'confirm')) return 'pending-request';
  return 'pending';
};

/** True iff the patient should see a Confirm button for this appointment. */
export const canRequestConfirm = (appt): boolean =>
  getConfirmationState(appt) === 'pending';
